// Libs
import React from 'react';
import { withRouter } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import moment from 'moment';

// Services & Helpers
import BaseService from 'services/BaseService';
import GlobalStateService from 'services/GlobalStateService';
import InstructorService from 'services/InstructorService';
import DateHelpers from 'helpers/DateHelpers';
import TextHelpers from 'helpers/TextHelpers';
import BootboxHelper from 'helpers/BootboxHelper';

// Components
import Loader from 'components/common/Loader';
import AvailabilitySelector from 'components/instructor/AvailabilitySelector';
import PageHeader from 'components/common/PageHeader';
import EditBookingModal from 'components/bookings/EditBookingModal';
import SubmitAvailabilityModal from 'components/instructor/SubmitAvailabilityModal';

//-------------------------------------------------------------------------------------------------------------------

class AvailabilitySelectorPage extends React.Component {

    constructor(props) {
        super(props);

        this.editBookingModalRef = React.createRef();
        this.submitAvailabilityModalRef = React.createRef();
        
        this.updateAvailability = this.updateAvailability.bind(this);
        this.setSpecialAvailabilityType = this.setSpecialAvailabilityType.bind(this);
        this.viewBooking = this.viewBooking.bind(this);

        this.state = {
            isLoading: true,
            workingDays: null,
            //showNonWorkingDays: false,
            datesAdded: [],
            datesRemoved: []
        };
    }

    componentDidMount() {
        window.scrollTo(0, 0);
        this.load(false);
    }

    async load(reset) {
        const loginInfo = GlobalStateService.getValue('loginInfo');
        const slug = this.props.match.params.slug || loginInfo.instructor.slug;

        GlobalStateService.setValues({
            area: (loginInfo.isAdmin ? 'instructors' : 'availability-selector')
        }); 
        const instructor = await BaseService.callAPI('instructor/get-basic-details/' + slug);
        const availability = await InstructorService.getFullAvailability(slug);
        
        let state = { ...this.state, availability, instructor };

        if (reset) {
            state.datesAdded = [];
            state.datesRemoved = [];
        } else {
            // Maintain changes from before load
            state.datesAdded.forEach(da => {
                const date = moment(da.date).toDate();
                const weekCommencing = moment(date);
                while (weekCommencing.format('E') != 1) {
                    weekCommencing.add(-1, 'day');
                }
                state = this.updateAvailability(state, weekCommencing.toDate(), date, 1, true);
            });
            state.datesRemoved.forEach(da => {
                const date = moment(da.date).toDate();
                const weekCommencing = moment(date);
                while (weekCommencing.format('E') != 1) {
                    weekCommencing.add(-1, 'day');
                }
                state = this.updateAvailability(state, weekCommencing.toDate(), date, 1, false);
            });
        }

        this.setState({
            ...state,
            isLoading: false
        });
    }

    //updateShowNonWorkingDays(showNonWorkingDays, availability) {
    //    if (!availability) {
    //        availability = this.state.availability;
    //    }
    //    const daysOfWeek = [ 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday' ];
    //    this.setState({
    //        showNonWorkingDays: showNonWorkingDays,
    //        workingDays: (showNonWorkingDays ? daysOfWeek : availability.instructor.workingDays)
    //    });
    //}

    async updateMode(isAvailByDefault) {
        const availability = { ...this.state.availability };
        const instructor = { ...availability.instructor };
        const currentValue = !!instructor.isAvailByDefault;
        instructor.isAvailByDefault = isAvailByDefault;
        availability.instructor = instructor;
        
        this.setState({
            availability
        }, async () => {
            const confirm = await BootboxHelper.confirm(
                'NOTE: changing your availability mode could lead to loss of availability information. ' +
                'Are you sure you want to continue?'
            );
            if (confirm) {
                await InstructorService.updateAvailabilityMode(instructor.slug, instructor.isAvailByDefault);
                this.setState({
                    isLoading: true
                });
                this.load(true);
            } else {
                const availability = { ...this.state.availability };
                const instructor = { ...availability.instructor };
                instructor.isAvailByDefault = currentValue;
                availability.instructor = instructor;
                this.setState({
                    availability
                });
            }
        });
    }

    setSpecialAvailabilityType(weekCommencing, date, numDays, specialAvailabilityType, notes) {
        weekCommencing = moment(weekCommencing).toDate().toISOString();
        if (!date) {
            date = weekCommencing;
        }
        const availability = { ...this.state.availability };
        const weekIndex = availability.weeks.findIndex(w => moment(w.weekCommencing).toDate().toISOString() == weekCommencing);
        const week = { ...availability.weeks[weekIndex] };

        // Enumerate each date in the range
        date = moment(date);
        for (let i = 0; i < numDays; i++) {
            const key = DateHelpers.getDateKey(date);

            // Toggle
            week.specialAvailabilityDates[key] = specialAvailabilityType;
            week.notes[key] = notes;
            
            // Update on server
            InstructorService.setSpecialAvailabilityType(
                this.state.availability.instructor.id,
                moment(date).format('YYYY-MM-DD'),
                specialAvailabilityType,
                notes
            );
            
            date.add(1, 'day');
        }

        // Update state
        availability.weeks[weekIndex] = week;
        return { availability };
    }

    updateAvailability(state, weekCommencing, date, numDays, isAvailable) {
        date = moment(date || weekCommencing);
        weekCommencing = moment(weekCommencing).toDate().toISOString();

        const availability = { ...state.availability };
        const weekIndex = availability.weeks.findIndex(w => moment(w.weekCommencing).toDate().toISOString() == weekCommencing);
        const week = { ...availability.weeks[weekIndex] };
        const datesAdded = [...state.datesAdded];
        const datesRemoved = [...state.datesRemoved];

        const workingDays = this.state.availability.instructor.workingDays;

        for (var i = 0; i < numDays; i++) {
            const key = DateHelpers.getDateKey(date);
            if (workingDays.indexOf(date.format('dddd')) != -1 &&
                !week.bookings[key] &&
                !week.pendingApprovalDates[key]
            ) {

                if (isAvailable) {
                    if (!week.availableDates[key]) {
                        week.availableDates[key] = true;
                        const datesRemovedIndex = datesRemoved.findIndex(d => d.date == key);
                        if (datesRemovedIndex == -1) {
                            if (datesAdded.findIndex(d => d.date == key) == -1) {
                                datesAdded.push({
                                    date: key,
                                    isLocked: !!week.lockedDates[key]
                                });
                            }
                        } else {
                            datesRemoved.splice(datesRemovedIndex, 1);
                        }
                    }
                } else if (week.availableDates[key]) {
                    delete week.availableDates[key];
                    const datesAddedIndex = datesAdded.findIndex(d => d.date == key);
                    if (datesAddedIndex == -1) {
                        if (datesRemoved.findIndex(d => d.date == key) == -1) {
                            datesRemoved.push({
                                date: key,
                                isLocked: !!week.lockedDates[key]
                            });
                        }
                    } else {
                        datesAdded.splice(datesAddedIndex, 1);
                    }
                }
            }

            date.add(1, 'day');
        }

        // Update state
        availability.weeks[weekIndex] = week;
        state = {
            availability,
            datesAdded,
            datesRemoved
        }

        return state;
    }

    async viewBooking(id) {
        await this.editBookingModalRef.current.show({
            bookingID: id
        });
        this.load(false);
    }

    showDates(type) {
        const title = 'Dates ' + (type == 'datesAdded' ? 'added' : 'removed');
        const datesSummary = TextHelpers.getDatesSummary(this.state[type], title);
        BootboxHelper.alert(datesSummary);
    }

    async confirmSubmit() {
        await this.submitAvailabilityModalRef.current.show({
            slug: this.state.availability.instructor.slug,
            datesAdded: this.state.datesAdded,
            datesRemoved: this.state.datesRemoved
        });
        this.load(true);
    }

    //-------------------------------------------------------------------------------------------------------------

    render() {
        const {
            availability,
            instructor,
            isLoading
        } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>
            <div className="page availability-selector-page">

                <PageHeader>
                    <h1>Calendar</h1>

                    <h2>
                        for {instructor.firstName} {instructor.lastName}
                        {availability && availability.dateAvailabilityUpdated && <>
                            {' '}(updated: {moment(availability.dateAvailabilityUpdated).format('DD/MM/YYYY')})
                        </>}
                    </h2>

                    <div className="buttons">

                        {this.renderButtons()}

                    </div>
                </PageHeader>

                <main>

                    <Container>

                        <section>

                            {this.renderInner()}

                        </section>

                    </Container>

                </main>

            </div>

            <EditBookingModal ref={this.editBookingModalRef} />
            <SubmitAvailabilityModal ref={this.submitAvailabilityModalRef} />

        </>);
    }

    renderInner() {
        const {
            isLoading,
            availability,
            //workingDays,
            //showNonWorkingDays
        } = this.state;

        if (isLoading) {
            return (<Loader />);
        }

        return (<>

            {/*
            <div className="panel mode-selector">

                <label>
                    Mode:
                </label>

                <select
                    value={`${availability.instructor.isAvailByDefault}`}
                    onChange={e => this.updateMode(e.target.value == 'true')}
                >
                    <option value="false">
                        Unavailable by default; I will select days that I am available
                    </option>
                    <option value="true">
                        Available by default; I will select days that I am unavailable
                    </option>
                </select>

            </div>
            */}

            {/*
            <div className="panel show-non-working-days-selector">

                <label>
                    <input type="checkbox" checked={showNonWorkingDays} onChange={e => this.updateShowNonWorkingDays(e.target.checked)} />
                    Show non-working days
                </label>

            </div>
            */}

            <AvailabilitySelector
                isScrollable
                workingDays={availability.instructor.workingDays}
                availability={availability}
                onUpdateAvailability={(weekCommencing, date, numDays, isAvailable) => {
                    const state = this.updateAvailability(this.state, weekCommencing, date, numDays, isAvailable);
                    this.setState(state);
                }}
                setSpecialAvailabilityType={(weekCommencing, date, numDays, specialAvailabilityType, notes) => {
                    const state = this.setSpecialAvailabilityType(weekCommencing, date, numDays, specialAvailabilityType, notes);
                    this.setState(state);
                }}
                onViewBooking={this.viewBooking}
            />

        </>);
    }

    renderButtons() {
        const { datesAdded, datesRemoved, numRemovedRequireApproval } = this.state;

        return (<>
            {datesAdded.length > 0 &&
                <span className="me-2">
                    {datesAdded.length} date{datesAdded.length != 1 && 's'} added{' '}
                    <button className="btn btn-sm fa-solid fa-search ms-1" onClick={e => this.showDates('datesAdded')}></button>
                </span>
            }

            {datesRemoved.length > 0 &&
                <span className="me-2">
                    {datesRemoved.length} date{datesRemoved.length != 1 && 's'} removed{' '}
                    {numRemovedRequireApproval > 0 && <>
                        ({numRemovedRequireApproval} require{numRemovedRequireApproval == 1 && 's'} approval){' '}
                    </>}
                    <button className="btn btn-sm fa-solid fa-search ms-1" onClick={e => this.showDates('datesRemoved')}></button>
                </span>
            }

            {(datesAdded.length > 0 || datesRemoved.length > 0) ? <>
                <button className="btn btn-primary" onClick={e => this.confirmSubmit()}>
                    Submit changes to vendor
                </button>
            </> :
                <span>No pending changes yet</span>
            }
        </>);
    }

}

export default withRouter(AvailabilitySelectorPage);